import type { NotificationView } from "@repo/data-store";

/**
 * Display copy for inbox items — labels and verbs only.
 *
 * Nothing here parses the row. Kind, source and occurrence are projected once
 * by `@repo/data-store` from `@repo/notify`'s own readers, so this file maps a
 * kind string to words and nothing else. Anything that needs to know the SHAPE
 * of a notification belongs in the package that mints them.
 */

/** Verb per fact kind. An unknown kind (an R6 ambient fact this build predates)
 *  degrades to a neutral verb rather than rendering nothing. */
const KIND_VERB: Record<string, string> = {
  mentioned: "mentioned you on",
  assigned: "assigned you to",
  "patient-checked-in": "checked in for",
  "results-arrived": "posted results for",
  "form-submitted": "submitted a form on",
  "message-received": "sent you a message about",
  "call-missed": "called you about",
};

/** Human label per fact kind, for the filter rail and item chips. */
const KIND_LABEL: Record<string, string> = {
  mentioned: "Mentions",
  assigned: "Assignments",
  "patient-checked-in": "Check-ins",
  "results-arrived": "Results",
  "form-submitted": "Forms",
  "message-received": "Messages",
  "call-missed": "Calls",
};

export function kindVerb(kind: string): string {
  return KIND_VERB[kind] ?? "notified you about";
}

/** Title-cases an unregistered kind (`visit-completed` → `Visit completed`) so a
 *  fact kind that ships after this build still gets a readable rail entry. */
export function kindLabel(kind: string): string {
  if (KIND_LABEL[kind]) return KIND_LABEL[kind];
  if (!kind) return "Other";
  const spaced = kind.replace(/[-_]/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function senderLabel(comm: NotificationView): string {
  const display = comm.sender?.display;
  return display && display !== "Someone" ? display : "System";
}

/**
 * What an item is ABOUT, in one phrase.
 *
 * Task rows get the MF number + description from the read-side join. Any other
 * source has no join (and shouldn't grow one per type) — naming the resource
 * keeps the row saying something instead of rendering a dash.
 */
export function subjectPhrase(item: NotificationView): string {
  const { number, description } = item.subjectTask ?? {};
  if (number) return description ? `${number} ${description}` : number;
  // A Task with no MF identifier still has a description — falling straight to
  // the resourceType here would drop it from the row AND from search.
  if (description) return description;
  return item.source?.resourceType ?? "something";
}

/** Day bucket for grouping. R8.4: the fact shape (kind + subject + occurredAt)
 *  must support grouping without re-parsing — this reads `sent` only. */
export function dayBucket(dateStr: string | undefined): string {
  if (!dateStr) return "Undated";
  const sent = new Date(dateStr);
  const today = new Date();
  const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (isSameDay(sent, today)) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(sent, yesterday)) return "Yesterday";
  return sent.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
